import {
  isHiddenGame,
  type GameStat,
  type MatchHistoryItem,
  type MatchResult,
  type PerGameStats,
  type UserStats,
} from "@bing/shared";
import type { ReactNode } from "react";
import { GAMES } from "../config";
import { faDigits } from "../i18n/digits";
import { fa } from "../i18n/fa";
import { useSession } from "../store/context";
import { GameMark } from "./GameMark";

/**
 * Shared stats + match-history presentation, reused by the Profile screen and the
 * lobby History screen so the two render identically from the same session store
 * slices (`stats`, `history`, …). Purely presentational — the owning screen is
 * responsible for kicking off `loadStats()` / `loadHistory()` on mount.
 */

/** The per-game stat keys, in display order (Blackjack is excluded from stats) — EVERY key of
 *  `PerGameStats`, hidden games included, so un-hiding one brings its row back by itself. */
export const STAT_GAME_ORDER: readonly (keyof PerGameStats)[] = [
  "hokm",
  "shelem",
  "pasur",
  "haftkhabis",
  "hearts",
  "bluff",
  "chess",
  // منچ is live + ACTIVE; its per-game stats exist in PerGameStats + the API, but were never listed
  // here, so the profile/History grid silently skipped its row. Placed before تخته نرد (board order).
  "mensch",
  // ⚠️ AND THEN دوز REPEATED IT, EXACTLY. The API serves `perGame.dooz` (it is a key of
  // `PerGameStats`, so the server side could not forget it) and the match LIST already renders
  // «دوز» from the lobby catalogue — only THIS list decides the breakdown, so a finished دوز match
  // showed up in تاریخچه and nowhere in «به تفکیک بازی», and the totals row exceeded the sum of the
  // rows by exactly those games. Right after منچ, mirroring the shared LOBBY_GAME_ORDER: «مار پله»
  // and «دوز» swapped places there (the operator, card-86 round 2).
  "dooz",
  "backgammon",
  // مار پله (card-86) — the board games' end, as the lobby's board tab has it since the swap.
  "ladders",
];

/** The rows «به تفکیک بازی» renders: {@link STAT_GAME_ORDER} minus the games hidden from players
 *  (`HIDDEN_GAMES` — a «🎭 بلوف ۰» row names بلوف on every profile, operator 2026-10-03). */
export const STAT_GAMES: readonly (keyof PerGameStats)[] = STAT_GAME_ORDER.filter(
  (id) => !isHiddenGame(id),
);

/** Persian name + emoji for a game id (from the lobby catalogue). */
const GAME_META: Readonly<Record<string, { fa: string; icon: string }>> = Object.fromEntries(
  GAMES.map((g) => [g.id, { fa: g.fa, icon: g.icon }]),
);

export function gameMeta(id: string): { fa: string; icon: string } {
  return GAME_META[id] ?? { fa: id, icon: "🎴" };
}

export const RESULT_LABEL: Record<MatchResult, string> = {
  win: fa.profile.resultWin,
  loss: fa.profile.resultLoss,
  draw: fa.profile.resultDraw,
};

/** A compact, RTL-friendly "how long ago" label (deterministic, no locale dep). */
export function formatWhen(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const days = Math.floor((Date.now() - then) / 86_400_000);
  if (days <= 0) return "امروز";
  if (days === 1) return "دیروز";
  return `${faDigits(days)} روز پیش`;
}

/** One stat number in a summary row. */
export function StatCard({
  label,
  value,
  accent,
  compact = false,
}: {
  label: string;
  value: number;
  accent: string;
  /** A smaller card (tighter padding + smaller numeral) for the Profile hero, so the
   *  games/wins/losses row takes less vertical space. The dedicated History screen
   *  keeps the full size. */
  compact?: boolean;
}): ReactNode {
  return (
    <div
      className={`flex flex-1 flex-col items-center rounded-lg border border-outline-variant/50 bg-surface-container ${
        compact ? "px-2 py-1" : "p-sm"
      }`}
    >
      <span
        className={`text-on-surface-variant ${compact ? "font-caption text-caption" : "mb-xs font-label-md text-label-md"}`}
      >
        {label}
      </span>
      <span
        className={`${accent} ${compact ? "font-headline-sm text-headline-sm" : "font-numeral-xl text-numeral-xl"}`}
      >
        {faDigits(value)}
      </span>
    </div>
  );
}

/** The games / wins / losses summary row. */
export function StatTotals({ stats }: { stats: UserStats }): ReactNode {
  return (
    <div className="flex w-full justify-center gap-md">
      <StatCard label={fa.profile.games} value={stats.gamesPlayed} accent="text-primary" />
      <StatCard label={fa.profile.wins} value={stats.wins} accent="text-secondary" />
      <StatCard label={fa.profile.losses} value={stats.losses} accent="text-error" />
    </div>
  );
}

/** The per-game wins/losses breakdown table. */
export function PerGameStats({ stats }: { stats: UserStats }): ReactNode {
  return (
    <section className="flex flex-col gap-sm">
      <h3 className="m-0 px-sm font-headline-sm text-headline-sm text-on-surface">
        {fa.profile.perGame}
      </h3>
      <div className="overflow-hidden rounded-xl bg-surface-container border border-outline-variant/30">
        {STAT_GAMES.map((id, i) => {
          const g: GameStat = stats.perGame[id];
          const meta = gameMeta(id);
          return (
            <div
              key={id}
              className={`flex items-center gap-sm p-md ${
                i < STAT_GAMES.length - 1 ? "border-b border-outline-variant/20" : ""
              }`}
            >
              <span aria-hidden="true" className="text-2xl leading-none">
                <GameMark icon={meta.icon} />
              </span>
              <span className="flex-1 font-body-lg text-body-lg text-on-surface">{meta.fa}</span>
              <span className="font-caption text-caption text-on-surface-variant">
                {fa.profile.games} {faDigits(g.gamesPlayed)}
              </span>
              <span className="min-w-[64px] text-left font-body-lg text-body-lg text-on-surface">
                <span className="text-success-win">{faDigits(g.wins)}</span>
                <span className="text-on-surface-variant"> / </span>
                <span className="text-error">{faDigits(g.losses)}</span>
                {/* Draws are shown ONLY when there are any. Only chess can produce one, so for
                    almost every user on almost every row the figure is a permanent ۰ — a third
                    number that is always zero teaches nothing and costs width on a row whose name
                    already truncates. Hiding it at zero also makes this change a no-op for those
                    rows: identical markup, identical geometry. The 🤝 is what says "draw"; the
                    green/red pair carries no label either, so a bare third number would be
                    ambiguous where this is not. */}
                {g.draws > 0 ? (
                  <span className="text-secondary" title={fa.profile.draws}>
                    {" · 🤝 "}
                    {faDigits(g.draws)}
                  </span>
                ) : null}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/** A pure list of match rows (icon + game + when + result pill). Shared by the store-
 *  connected {@link MatchHistory} and the read-only profile-VIEW history, so both render
 *  identically. */
export function MatchList({ items }: { items: readonly MatchHistoryItem[] }): ReactNode {
  return (
    <ul className="m-0 list-none overflow-hidden rounded-xl bg-surface-container border border-outline-variant/30 p-0">
      {items.map((m, i) => {
        const meta = gameMeta(m.game);
        const win = m.result === "win";
        return (
          <li
            key={m.id}
            className={`flex items-center gap-sm p-md ${
              i < items.length - 1 ? "border-b border-outline-variant/20" : ""
            }`}
          >
            <span aria-hidden="true" className="text-2xl leading-none">
              <GameMark icon={meta.icon} />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block font-body-lg text-body-lg text-on-surface">{meta.fa}</span>
              <span className="block font-caption text-caption text-on-surface-variant">
                {formatWhen(m.endedAt)}
              </span>
            </span>
            <span
              className={`rounded-full px-3 py-1 font-label-md text-label-md ${
                win
                  ? "bg-success-win/20 text-success-win"
                  : m.result === "loss"
                    ? "bg-error/20 text-error"
                    : "bg-surface-variant text-on-surface-variant"
              }`}
            >
              {RESULT_LABEL[m.result]}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/** The match-history list (heading + loading/empty/error states + paged rows).
 * Reads its rows from the session store; the owning screen triggers the load. */
export function MatchHistory(): ReactNode {
  const history = useSession((s) => s.history);
  const historyCursor = useSession((s) => s.historyCursor);
  const historyLoaded = useSession((s) => s.historyLoaded);
  const historyLoading = useSession((s) => s.historyLoading);
  const historyError = useSession((s) => s.historyError);
  const loadMoreHistory = useSession((s) => s.loadMoreHistory);

  return (
    <section className="flex flex-col gap-sm pb-md">
      <h3 className="m-0 px-sm font-headline-sm text-headline-sm text-on-surface">
        {fa.profile.history}
      </h3>
      {historyError ? (
        <p className="m-0 px-sm font-body-md text-body-md text-error">{fa.profile.historyError}</p>
      ) : !historyLoaded && historyLoading ? (
        <p className="m-0 px-sm font-body-md text-body-md text-on-surface-variant">
          {fa.profile.loadingHistory}
        </p>
      ) : history.length === 0 ? (
        <div className="rounded-xl border border-dashed border-outline-variant/50 p-lg text-center font-body-md text-body-md text-on-surface-variant">
          {fa.profile.noHistory}
        </div>
      ) : (
        <>
          <MatchList items={history} />
          {historyCursor ? (
            <button
              type="button"
              disabled={historyLoading}
              onClick={() => void loadMoreHistory()}
              className="mx-auto mt-xs appearance-none cursor-pointer rounded-lg border border-outline-variant bg-surface-container-high px-lg py-sm font-label-md text-label-md text-on-surface transition-colors hover:bg-surface-variant disabled:opacity-70"
            >
              {historyLoading ? fa.profile.loadingHistory : fa.profile.loadMore}
            </button>
          ) : null}
        </>
      )}
    </section>
  );
}
